import { useState, useCallback } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN");

const COURIERS = [
  { id:"shiprocket", name:"Shiprocket",  color:"#e8460f", bg:"#fff3ee" },
  { id:"delhivery",  name:"Delhivery",   color:"#D42B31", bg:"#fff0f0" },
  { id:"bluedart",   name:"Blue Dart",   color:"#1a237e", bg:"#eef0ff" },
  { id:"ekart",      name:"Ekart",       color:"#2874F0", bg:"#ebf3ff" },
];

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:13, verticalAlign:"middle" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600 }}>{label}</span>
);

const EMPTY_SHIP = {
  order_id:"", channel_id:"", pickup_postcode:"201001",
  delivery_postcode:"", weight:0.5, length:20, breadth:15, height:5,
  billing_customer_name:"", billing_address:"", billing_city:"", billing_state:"",
  billing_pincode:"", billing_email:"", billing_phone:"",
  order_items:[{ name:"", sku:"", units:1, selling_price:0 }],
  payment_method:"prepaid", sub_total:0, cod_amount:0,
};

export default function PageLogistics({ toast, orders=[], products=[] }) {
  const [tab,         setTab]         = useState("rates");
  const [aiDelay,     setAiDelay]     = useState(null);
  const [aiLoading,   setAiLoading]   = useState(false);
  const [delayForm,   setDelayForm]   = useState({
    fromPincode: "201001",
    toPincode:   "",
    toCity:      "",
    toState:     "",
    courier:     "all",
    weight:      0.5,
    orderValue:  500,
    paymentType: "prepaid",
    productType: "belt",
  });
  const [batchPincodes, setBatchPincodes] = useState("");
  const [batchMode,     setBatchMode]     = useState(false);
  const [batchResult,   setBatchResult]   = useState(null);
  const [shipForm,    setShipForm]    = useState(EMPTY_SHIP);
  const [rateForm,    setRateForm]    = useState({ pickup_postcode:"201001", delivery_postcode:"", weight:0.5, cod:false });
  const [rates,       setRates]       = useState([]);
  const [trackAwb,    setTrackAwb]    = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [shipments,   setShipments]   = useState([]);
  const [codSummary,  setCodSummary]  = useState(null);
  const [loading,     setLoading]     = useState("");
  const [labelUrl,    setLabelUrl]    = useState("");
  const [selectedCourier, setSelectedCourier] = useState(null);

  // ── rate comparison ───────────────────────────────────────────
  const compareRates = async () => {
    if (!rateForm.delivery_postcode) { toast("Enter delivery pincode", "error"); return; }
    setLoading("rates");
    setRates([]);
    try {
      const data = await api("/logistics/rates/compare", {
        method: "POST",
        body: JSON.stringify(rateForm),
      });
      setRates(data.rates || []);
      if (!data.rates?.length) toast("No rates found for this route", "error");
      else toast(data.count + " rates found ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── create shipment ───────────────────────────────────────────
  const createShipment = async () => {
    if (!shipForm.billing_customer_name) { toast("Enter customer name", "error"); return; }
    if (!shipForm.delivery_postcode)     { toast("Enter delivery pincode", "error"); return; }
    setLoading("create");
    try {
      const data = await api("/logistics/shiprocket/create", {
        method: "POST",
        body: JSON.stringify(shipForm),
      });
      toast("Shipment created ✓ — Order ID: " + (data.data?.order_id || ""), "success");
      setShipForm(EMPTY_SHIP);
      loadShipments();
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── fill from order ───────────────────────────────────────────
  const fillFromOrder = (orderName) => {
    const o = orders.find((x) => x.name === orderName || String(x.id) === orderName);
    if (!o) return;
    const addr = o.shipping_address || {};
    const item = o.line_items?.[0] || {};
    setShipForm((f) => ({
      ...f,
      order_id:               o.name || String(o.id),
      billing_customer_name:  o.customer ? o.customer.first_name + " " + o.customer.last_name : "",
      billing_email:          o.customer?.email || "",
      billing_phone:          addr.phone || "",
      billing_address:        addr.address1 || "",
      billing_city:           addr.city || "",
      billing_state:          addr.province || "",
      billing_pincode:        addr.zip || "",
      delivery_postcode:      addr.zip || "",
      sub_total:              parseFloat(o.total_price || 0),
      cod_amount:             o.payment_gateway_names?.includes("Cash on Delivery") ? parseFloat(o.total_price) : 0,
      payment_method:         o.payment_gateway_names?.includes("Cash on Delivery") ? "COD" : "prepaid",
      order_items: (o.line_items || []).map((li) => ({
        name:          li.name,
        sku:           li.sku || li.variant_id,
        units:         li.quantity,
        selling_price: parseFloat(li.price),
      })),
    }));
    toast("Order details filled ✓", "success");
  };

  // ── track shipment ────────────────────────────────────────────
  const trackShipment = async () => {
    if (!trackAwb) { toast("Enter AWB number", "error"); return; }
    setLoading("track");
    setTrackResult(null);
    try {
      const data = await api("/logistics/track/" + trackAwb);
      setTrackResult(data.tracking);
      toast("Tracking found — " + data.tracking.courier, "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── load shipments ────────────────────────────────────────────
  const loadShipments = async () => {
    setLoading("shipments");
    try {
      const data = await api("/logistics/shiprocket/shipments");
      setShipments(data.shipments || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── download label ────────────────────────────────────────────
  const downloadLabel = async (shipmentId) => {
    setLoading("label_" + shipmentId);
    try {
      const data = await api("/logistics/shiprocket/label", {
        method: "POST",
        body: JSON.stringify({ shipmentId }),
      });
      if (data.labelUrl) {
        window.open(data.labelUrl, "_blank");
        toast("Label opened ✓", "success");
      } else {
        toast("Label not ready yet", "error");
      }
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── schedule pickup ───────────────────────────────────────────
  const schedulePickup = async (shipmentId) => {
    setLoading("pickup_" + shipmentId);
    try {
      await api("/logistics/shiprocket/pickup", {
        method: "POST",
        body: JSON.stringify({ shipmentIds: [shipmentId] }),
      });
      toast("Pickup scheduled ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  // ── COD summary ───────────────────────────────────────────────
  const loadCodSummary = async () => {
    setLoading("cod");
    try {
      const data = await api("/logistics/cod/summary");
      setCodSummary(data.summary);
      toast("COD summary loaded ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  const updateItem = (i, key, val) => setShipForm((f) => ({
    ...f,
    order_items: f.order_items.map((it, idx) => idx===i ? { ...it, [key]: val } : it),
  }));

  const COURIERS = ["Shiprocket","Delhivery","Bluedart","Ecom Express","DTDC","XpressBees","Shadow Fax"];

  const predictDelay = async () => {
    if (!delayForm.toPincode) { toast("Enter delivery pincode", "error"); return; }
    setAiLoading(true);
    try {
      // Fetch existing shipment history to get real delay data
      let shipmentHistory = [];
      try {
        const data = await api("/logistics/shiprocket/shipments");
        shipmentHistory = (data.shipments || []).slice(0, 100).map((s) => ({
          courier:         s.courier || s.courier_name,
          toPincode:       s.delivery_postcode || s.delivery_address?.pincode,
          toCity:          s.delivery_city || s.delivery_address?.city,
          toState:         s.delivery_state || s.delivery_address?.state,
          status:          s.status,
          createdAt:       s.created_at,
          deliveredAt:     s.delivered_date || s.deliver_date,
          isDelayed:       s.status === "delayed" || s.status === "RTO" || s.status === "returned",
          daysToDeliver:   s.delivered_date && s.created_at
            ? Math.ceil((new Date(s.delivered_date)-new Date(s.created_at))/86400000)
            : null,
        }));
      } catch(e) { /* no shipment history */ }

      const response = await fetch(API + "/ai/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + getToken() },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `You are a logistics and delivery prediction expert for Indian ecommerce. The seller is based in Ghaziabad, UP (pincode 201001).

Predict courier delivery performance for this shipment:
- From: Ghaziabad, UP 201001
- To Pincode: ${delayForm.toPincode}
- To City/State: ${delayForm.toCity || "Unknown"}, ${delayForm.toState || "Unknown"}
- Weight: ${delayForm.weight} kg
- Order Value: ₹${delayForm.orderValue}
- Payment: ${delayForm.paymentType}
- Product: ${delayForm.productType}
- Couriers to evaluate: ${delayForm.courier === "all" ? "Shiprocket, Delhivery, Bluedart, Ecom Express, DTDC, XpressBees" : delayForm.courier}

Historical shipment data from this seller:
${shipmentHistory.length > 0 ? JSON.stringify(shipmentHistory.slice(0,20), null, 2) : "No historical data available — use general knowledge about Indian courier performance."}

Based on:
1. Known courier performance by zone (metro/tier1/tier2/tier3/remote)
2. Pincode area analysis (is it metro, tier-2, rural, hilly, NE India, J&K?)
3. Historical patterns if available
4. Day-of-week and seasonal factors
5. COD vs prepaid delivery success rates

Return ONLY a JSON object (no markdown):
{
  "pincodeAnalysis": {
    "pincode": "${delayForm.toPincode}",
    "city": "string",
    "state": "string",
    "zone": "metro|tier1|tier2|tier3|remote|hilly|northeast",
    "deliverabilityScore": number (0-100),
    "knownIssues": ["string"],
    "rtoRisk": "low|medium|high",
    "codSuccessRate": "string like '78%'"
  },
  "courierPredictions": [
    {
      "courier": "string",
      "available": true,
      "estimatedDays": number,
      "minDays": number,
      "maxDays": number,
      "delayProbability": "string like '15%'",
      "onTimeScore": number (0-100),
      "rtoRate": "string like '8%'",
      "estimatedCost": number,
      "strengths": ["string"],
      "weaknesses": ["string"],
      "recommended": true or false,
      "recommendationReason": "string"
    }
  ],
  "bestCourier": "string",
  "etaAdvice": "string — what ETA to show customers",
  "riskFactors": ["string"],
  "customerMessageTemplate": "string — ready-to-use message for customer about expected delivery",
  "tips": ["string — actionable tips to reduce delay/RTO for this route"]
}`
          }]
        })
      });

      const data = await response.json();
      const text = data.content?.[0]?.text || "{}";
      const clean = text.replace(/```json|```/g,"").trim();
      setAiDelay(JSON.parse(clean));
      toast("✅ Delivery prediction ready", "success");
    } catch(e) { toast("Prediction failed: "+e.message, "error"); }
    setAiLoading(false);
  };

  const predictBatch = async () => {
    const pincodes = batchPincodes.split(/[\n,]/).map((p)=>p.trim()).filter((p)=>p.match(/^\d{6}$/));
    if (!pincodes.length) { toast("Enter valid 6-digit pincodes", "error"); return; }
    if (pincodes.length > 20) { toast("Max 20 pincodes at once", "error"); return; }
    setAiLoading(true);
    try {
      const response = await fetch(API + "/ai/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + getToken() },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `Analyse delivery performance for these Indian pincodes shipping from Ghaziabad UP 201001.
Pincodes: ${pincodes.join(", ")}

For each pincode return its zone, best courier, estimated days, RTO risk and deliverability score.

Return ONLY a JSON array (no markdown):
[{
  "pincode": "string",
  "city": "string",
  "state": "string",
  "zone": "metro|tier1|tier2|tier3|remote",
  "bestCourier": "string",
  "estimatedDays": number,
  "rtoRisk": "low|medium|high",
  "deliverabilityScore": number,
  "notes": "string"
}]`
          }]
        })
      });
      const data = await response.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/```json|```/g,"").trim();
      setBatchResult(JSON.parse(clean));
      toast("Batch analysis complete — "+pincodes.length+" pincodes", "success");
    } catch(e) { toast("Batch failed: "+e.message, "error"); }
    setAiLoading(false);
  };

  return (
    <div>
      {/* courier status row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {COURIERS.map((c) => (
          <div key={c.id} style={{ ...S.kpi, borderTop:`3px solid ${c.color}` }}>
            <div style={{ fontWeight:600, color:c.color, marginBottom:6 }}>{c.name}</div>
            <div style={{ fontSize:11, color:"#9ca3af" }}>Configure in Settings</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"rates",     label:"💰 Rate Comparison" },
          { id:"ai_delay",  label:"🤖 Delay Prediction"  },
          { id:"create",    label:"📦 Create Shipment" },
          { id:"shipments", label:"🚚 Shipments"       },
          { id:"track",     label:"🔍 Track AWB"       },
          { id:"cod",       label:"💵 COD Remittance"  },
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => { setTab(t.id); if(t.id==="shipments") loadShipments(); if(t.id==="cod") loadCodSummary(); if(t.id==="ai_delay") { setAiDelay(null); setBatchResult(null); } }}>{t.label}</button>
        ))}
      </div>

      {/* ══ RATE COMPARISON ══ */}
      {tab === "rates" && (
        <>
          <div style={S.card}>
            <div style={S.cardHdr}><span>Compare Rates Across Couriers</span></div>
            <div style={{ padding:16 }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
                <div>
                  <label style={S.label}>Pickup Pincode</label>
                  <input style={S.input} placeholder="201001" value={rateForm.pickup_postcode}
                    onChange={(e) => setRateForm((f) => ({ ...f, pickup_postcode:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Delivery Pincode *</label>
                  <input style={S.input} placeholder="400001" value={rateForm.delivery_postcode}
                    onChange={(e) => setRateForm((f) => ({ ...f, delivery_postcode:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Weight (kg)</label>
                  <input type="number" step="0.1" style={S.input} value={rateForm.weight}
                    onChange={(e) => setRateForm((f) => ({ ...f, weight:parseFloat(e.target.value)||0.5 }))}/>
                </div>
                <div>
                  <label style={S.label}>Payment Type</label>
                  <select style={S.input} value={rateForm.cod?"COD":"Prepaid"}
                    onChange={(e) => setRateForm((f) => ({ ...f, cod:e.target.value==="COD" }))}>
                    <option value="Prepaid">Prepaid</option>
                    <option value="COD">COD</option>
                  </select>
                </div>
              </div>
              <button style={{ ...S.btn(), padding:"10px 28px", fontSize:14, opacity:loading==="rates"?0.7:1 }}
                onClick={compareRates} disabled={loading==="rates"}>
                {loading==="rates" ? "Comparing…" : "⚖ Compare Rates"}
              </button>
            </div>
          </div>

          {rates.length > 0 && (
            <div style={S.card}>
              <div style={S.cardHdr}><span>Rate Results</span><span style={{ fontSize:12, color:"#9ca3af" }}>{rates.length} options found</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Courier","Provider","Rate","COD","ETD",""].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {rates.map((r, i) => (
                      <tr key={i} style={{ background: r.cheapest?"#f0fdf4":i===0?"#fff":"" }}
                        onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"}
                        onMouseLeave={(e) => e.currentTarget.style.background=r.cheapest?"#f0fdf4":""}>
                        <td style={{ ...S.td, fontWeight:600 }}>{r.courier}</td>
                        <td style={S.td}>{r.provider}</td>
                        <td style={{ ...S.td, fontWeight:700, color:r.cheapest?"#15803d":"#F47920" }}>
                          {INR(r.rate)}
                          {r.cheapest && <span style={{ marginLeft:6, fontSize:10, background:"#dcfce7", color:"#15803d", padding:"1px 6px", borderRadius:10 }}>Cheapest</span>}
                          {r.recommended && <span style={{ marginLeft:6, fontSize:10, background:"#dbeafe", color:"#2563eb", padding:"1px 6px", borderRadius:10 }}>Recommended</span>}
                        </td>
                        <td style={S.td}>{r.cod ? <Badge label="COD ✓" color="#15803d" bg="#f0fdf4"/> : <Badge label="Prepaid" color="#6b7280" bg="#f3f4f6"/>}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{r.etd || "—"}</td>
                        <td style={S.td}>
                          <button style={{ ...S.btn(), fontSize:11, padding:"4px 10px" }}
                            onClick={() => { setSelectedCourier(r); setTab("create"); toast(r.provider + " selected ✓", "success"); }}>
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══ CREATE SHIPMENT ══ */}
      {tab === "create" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>New Shipment</span>
                {selectedCourier && <Badge label={selectedCourier.provider + " — " + INR(selectedCourier.rate)} color="#15803d" bg="#f0fdf4"/>}
              </div>
              <div style={{ padding:16 }}>
                {/* fill from order */}
                <label style={S.label}>Auto-fill from Order</label>
                <input style={S.input} placeholder="Order # e.g. #1001" list="ord-list"
                  onChange={(e) => fillFromOrder(e.target.value)}/>
                <datalist id="ord-list">{orders.map((o,i) => <option key={i} value={o.name||o.id}/>)}</datalist>

                <div style={{ fontWeight:600, fontSize:12, margin:"6px 0 10px", color:"#374151" }}>Customer Details</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[
                    { label:"Name *",    key:"billing_customer_name", placeholder:"Customer name" },
                    { label:"Phone",     key:"billing_phone",         placeholder:"98XXXXXXXX" },
                    { label:"Email",     key:"billing_email",         placeholder:"customer@email.com" },
                    { label:"City",      key:"billing_city",          placeholder:"Mumbai" },
                    { label:"State",     key:"billing_state",         placeholder:"Maharashtra" },
                    { label:"Pincode *", key:"billing_pincode",       placeholder:"400001" },
                  ].map(({ label, key, placeholder }) => (
                    <div key={key}>
                      <label style={S.label}>{label}</label>
                      <input style={S.input} placeholder={placeholder} value={shipForm[key]}
                        onChange={(e) => setShipForm((f) => ({ ...f, [key]:e.target.value, ...(key==="billing_pincode"?{delivery_postcode:e.target.value}:{}) }))}/>
                    </div>
                  ))}
                </div>
                <label style={S.label}>Address</label>
                <input style={S.input} placeholder="Full address" value={shipForm.billing_address}
                  onChange={(e) => setShipForm((f) => ({ ...f, billing_address:e.target.value }))}/>
              </div>
            </div>
          </div>

          <div>
            <div style={S.card}>
              <div style={S.cardHdr}><span>Package & Items</span></div>
              <div style={{ padding:16 }}>
                {/* dimensions */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
                  {[
                    { label:"Weight (kg)", key:"weight", type:"number" },
                    { label:"Length (cm)", key:"length", type:"number" },
                    { label:"Width (cm)",  key:"breadth", type:"number" },
                    { label:"Height (cm)", key:"height", type:"number" },
                  ].map(({ label, key, type }) => (
                    <div key={key}>
                      <label style={S.label}>{label}</label>
                      <input type={type} style={S.input} value={shipForm[key]}
                        onChange={(e) => setShipForm((f) => ({ ...f, [key]:parseFloat(e.target.value)||0 }))}/>
                    </div>
                  ))}
                </div>

                {/* items */}
                <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>Items</div>
                {shipForm.order_items.map((item, i) => (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:8, marginBottom:8 }}>
                    <div>
                      {i===0 && <label style={S.label}>Product</label>}
                      <input style={S.input} placeholder="Product name" value={item.name} list="prod-list"
                        onChange={(e) => updateItem(i,"name",e.target.value)}/>
                    </div>
                    <div>
                      {i===0 && <label style={S.label}>Qty</label>}
                      <input type="number" min="1" style={S.input} value={item.units}
                        onChange={(e) => updateItem(i,"units",parseInt(e.target.value)||1)}/>
                    </div>
                    <div>
                      {i===0 && <label style={S.label}>Price (₹)</label>}
                      <input type="number" style={S.input} value={item.selling_price}
                        onChange={(e) => updateItem(i,"selling_price",parseFloat(e.target.value)||0)}/>
                    </div>
                  </div>
                ))}
                <datalist id="prod-list">{products.map((p) => <option key={p.id} value={p.name}/>)}</datalist>
                <button style={{ ...S.btnOut, fontSize:11, marginBottom:12 }}
                  onClick={() => setShipForm((f) => ({ ...f, order_items:[...f.order_items,{name:"",sku:"",units:1,selling_price:0}] }))}>
                  + Add Item
                </button>

                {/* payment */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                  <div>
                    <label style={S.label}>Payment Method</label>
                    <select style={S.input} value={shipForm.payment_method}
                      onChange={(e) => setShipForm((f) => ({ ...f, payment_method:e.target.value }))}>
                      <option value="prepaid">Prepaid</option>
                      <option value="COD">COD</option>
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Order Total (₹)</label>
                    <input type="number" style={S.input} value={shipForm.sub_total}
                      onChange={(e) => setShipForm((f) => ({ ...f, sub_total:parseFloat(e.target.value)||0 }))}/>
                  </div>
                </div>

                <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14, opacity:loading==="create"?0.7:1 }}
                  onClick={createShipment} disabled={loading==="create"}>
                  {loading==="create" ? "Creating…" : "🚀 Create Shipment on Shiprocket"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ SHIPMENTS ══ */}
      {tab === "shipments" && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span>Shipments ({shipments.length})</span>
            <button style={S.btn()} onClick={loadShipments} disabled={loading==="shipments"}>
              {loading==="shipments" ? "Loading…" : "↻ Refresh"}
            </button>
          </div>
          {loading==="shipments" ? (
            <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading shipments…</div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Order","AWB","Courier","Customer","Status","Created","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {shipments.map((s, i) => (
                    <tr key={i} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{s.order_id}</td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{s.awb_code || "—"}</td>
                      <td style={S.td}>{s.courier_name || "—"}</td>
                      <td style={{ ...S.td, fontWeight:500 }}>{s.customer_name}</td>
                      <td style={S.td}>
                        <Badge label={s.status || "created"}
                          color={s.status==="Delivered"?"#15803d":s.status==="In Transit"?"#2563eb":"#d97706"}
                          bg={s.status==="Delivered"?"#f0fdf4":s.status==="In Transit"?"#eff6ff":"#fffbeb"}/>
                      </td>
                      <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>
                        {s.created_at ? new Date(s.created_at).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td style={S.td}>
                        <div style={{ display:"flex", gap:4 }}>
                          {s.awb_code && (
                            <button style={{ ...S.btnOut, fontSize:10 }} onClick={() => { setTrackAwb(s.awb_code); setTab("track"); }}>
                              Track
                            </button>
                          )}
                          {s.id && (
                            <button style={{ ...S.btn("#2563eb"), fontSize:10, padding:"3px 8px" }}
                              onClick={() => downloadLabel(s.id)} disabled={loading==="label_"+s.id}>
                              {loading==="label_"+s.id ? "…" : "⬇ Label"}
                            </button>
                          )}
                          {s.id && (
                            <button style={{ ...S.btn("#15803d"), fontSize:10, padding:"3px 8px" }}
                              onClick={() => schedulePickup(s.id)} disabled={loading==="pickup_"+s.id}>
                              {loading==="pickup_"+s.id ? "…" : "📦 Pickup"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shipments.length === 0 && (
                    <tr><td colSpan={7} style={{ padding:0 }}>
                      <EmptyState
                        icon="🚚"
                        title="No shipments yet"
                        subtitle="Create shipments, generate AWB numbers, and track every parcel across couriers — all from here."
                        columns={["Order","Customer","Courier","AWB","Status"]}
                        exampleRows={[
                          ["#1042","Mahesh S.","Delhivery","DL98765432","In Transit"],
                          ["#1041","Priya M.","Bluedart","BD12345678","Delivered"],
                          ["#1040","Rahul V.","Shiprocket","SR55512345","Picked Up"],
                        ]}
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ TRACK ══ */}
      {tab === "track" && (
        <div style={{ maxWidth:600 }}>
          <div style={S.card}>
            <div style={S.cardHdr}><span>Track Shipment</span></div>
            <div style={{ padding:16 }}>
              <label style={S.label}>AWB / Tracking Number</label>
              <div style={{ display:"flex", gap:8 }}>
                <input style={{ ...S.input, flex:1, marginBottom:0 }} placeholder="Enter AWB number…"
                  value={trackAwb} onChange={(e) => setTrackAwb(e.target.value)}
                  onKeyDown={(e) => e.key==="Enter" && trackShipment()}/>
                <button style={{ ...S.btn(), opacity:loading==="track"?0.7:1 }}
                  onClick={trackShipment} disabled={loading==="track"}>
                  {loading==="track" ? "Tracking…" : "Track"}
                </button>
              </div>
              <div style={{ fontSize:11, color:"#9ca3af", marginTop:6 }}>
                Works with Shiprocket and Delhivery AWBs automatically
              </div>
            </div>
          </div>

          {trackResult && (
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Tracking Result</span>
                <Badge label={trackResult.courier} color="#2563eb" bg="#eff6ff"/>
              </div>
              <div style={{ padding:16 }}>
                {/* status */}
                <div style={{ background:"#f0fdf4", borderRadius:8, padding:14, marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:16, color:"#15803d", marginBottom:4 }}>{trackResult.status || "In Transit"}</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>
                    {trackResult.location && <span>📍 {trackResult.location}</span>}
                    {trackResult.etd && <span style={{ marginLeft:12 }}>📅 Expected: {trackResult.etd}</span>}
                  </div>
                </div>

                {/* history */}
                {trackResult.history?.length > 0 && (
                  <>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Tracking History</div>
                    {trackResult.history.map((h, i) => (
                      <div key={i} style={{ display:"flex", gap:12, marginBottom:10, paddingBottom:10, borderBottom:"1px solid #f3f4f6" }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:i===0?"#22c55e":"#d1d5db", marginTop:4, flexShrink:0 }}/>
                        <div>
                          <div style={{ fontWeight:500, fontSize:13 }}>{h.status}</div>
                          <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>
                            {h.location && <span>📍 {h.location}</span>}
                            {h.date && <span style={{ marginLeft:8 }}>🕐 {h.date}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ COD REMITTANCE ══ */}
      {tab === "cod" && (
        <>
          {codSummary ? (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:14 }}>
                {[
                  { label:"Total COD",    value:INR(codSummary.total),   color:"#F47920" },
                  { label:"Pending",      value:INR(codSummary.pending),  color:"#d97706" },
                  { label:"Settled",      value:INR(codSummary.settled),  color:"#15803d" },
                ].map((k) => (
                  <div key={k.label} style={S.kpi}>
                    <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
                    <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
                  </div>
                ))}
              </div>

              {codSummary.couriers?.length > 0 && (
                <div style={S.card}>
                  <div style={S.cardHdr}><span>COD by Courier</span></div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>
                        {["Courier","Total COD","Pending","Settled","Last Remittance"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {codSummary.couriers.map((c, i) => (
                          <tr key={i}>
                            <td style={{ ...S.td, fontWeight:600 }}>{c.courier}</td>
                            <td style={{ ...S.td, fontWeight:600, color:"#F47920" }}>{INR(c.total)}</td>
                            <td style={{ ...S.td, color:"#d97706" }}>{INR(c.pending)}</td>
                            <td style={{ ...S.td, color:"#15803d" }}>{INR(c.settled)}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{c.lastDate || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign:"center", padding:60 }}>
              <div style={{ fontSize:40, marginBottom:16 }}>💵</div>
              <div style={{ fontWeight:600, fontSize:16, marginBottom:8 }}>COD Remittance Summary</div>
              <div style={{ fontSize:13, color:"#6b7280", marginBottom:20 }}>
                Track all your COD collections and settlements across couriers
              </div>
              <button style={{ ...S.btn(), padding:"12px 32px", fontSize:14, opacity:loading==="cod"?0.7:1 }}
                onClick={loadCodSummary} disabled={loading==="cod"}>
                {loading==="cod" ? "Loading…" : "Load COD Summary"}
              </button>
            </div>
          )}
        </>
      )}

      {/* ══ AI COURIER DELAY PREDICTION ══ */}
      {tab === "ai_delay" && (
        <div>
          {/* Mode toggle */}
          <div style={{ display:"flex", gap:8, marginBottom:14 }}>
            <button style={{ ...S.btn(batchMode?"#e5e7eb":"#6366f1"), color:batchMode?"#374151":"#fff", fontSize:12 }} onClick={()=>{ setBatchMode(false); setBatchResult(null); }}>
              🔍 Single Pincode
            </button>
            <button style={{ ...S.btn(batchMode?"#6366f1":"#e5e7eb"), color:batchMode?"#fff":"#374151", fontSize:12 }} onClick={()=>{ setBatchMode(true); setAiDelay(null); }}>
              📋 Batch Pincodes
            </button>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:16, alignItems:"start" }}>
            {/* Left panel */}
            <div>
              {!batchMode ? (
                <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 }}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>🔍 Predict Delivery</div>
                  <div style={{ padding:16, display:"flex", flexDirection:"column", gap:10 }}>
                    {[
                      ["From Pincode","fromPincode","text","201001"],
                      ["To Pincode *","toPincode","text","e.g. 400001"],
                      ["To City","toCity","text","e.g. Mumbai"],
                      ["To State","toState","text","e.g. Maharashtra"],
                    ].map(([label,key,type,ph])=>(
                      <div key={key}>
                        <label style={S.label}>{label}</label>
                        <input type={type} style={S.input} placeholder={ph} value={delayForm[key]}
                          onChange={(e)=>setDelayForm((f)=>({...f,[key]:e.target.value}))}/>
                      </div>
                    ))}
                    <div>
                      <label style={S.label}>Courier</label>
                      <select style={S.input} value={delayForm.courier} onChange={(e)=>setDelayForm((f)=>({...f,courier:e.target.value}))}>
                        <option value="all">All Couriers</option>
                        {COURIERS.map((c)=><option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <div>
                        <label style={S.label}>Weight (kg)</label>
                        <input type="number" min="0.1" step="0.1" style={S.input} value={delayForm.weight}
                          onChange={(e)=>setDelayForm((f)=>({...f,weight:parseFloat(e.target.value)||0.5}))}/>
                      </div>
                      <div>
                        <label style={S.label}>Order Value (₹)</label>
                        <input type="number" style={S.input} value={delayForm.orderValue}
                          onChange={(e)=>setDelayForm((f)=>({...f,orderValue:parseFloat(e.target.value)||0}))}/>
                      </div>
                    </div>
                    <div>
                      <label style={S.label}>Payment Type</label>
                      <div style={{ display:"flex", gap:6 }}>
                        {["prepaid","cod"].map((v)=>(
                          <button key={v} onClick={()=>setDelayForm((f)=>({...f,paymentType:v}))}
                            style={{ flex:1, padding:"7px", borderRadius:7, border:"1.5px solid "+(delayForm.paymentType===v?"#6366f1":"#e5e7eb"), background:delayForm.paymentType===v?"#f5f3ff":"#fff", color:delayForm.paymentType===v?"#6366f1":"#374151", fontWeight:delayForm.paymentType===v?700:400, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
                            {v==="prepaid"?"💳 Prepaid":"💵 COD"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button style={{ ...S.btn("#6366f1"), padding:"10px", fontSize:13, opacity:aiLoading?0.7:1 }}
                      onClick={predictDelay} disabled={aiLoading}>
                      {aiLoading ? "🤖 Predicting…" : "🤖 Predict Delivery"}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 }}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📋 Batch Pincode Analysis</div>
                  <div style={{ padding:16 }}>
                    <label style={S.label}>Enter Pincodes (one per line or comma-separated, max 20)</label>
                    <textarea style={{ ...S.input, height:160, resize:"vertical", fontFamily:"monospace", fontSize:12 }}
                      placeholder={"400001\n110001\n560001\n700001\n600001"}
                      value={batchPincodes} onChange={(e)=>setBatchPincodes(e.target.value)}/>
                    <div style={{ fontSize:11, color:"#9ca3af", margin:"6px 0 10px" }}>
                      {batchPincodes.split(/[\n,]/).filter((p)=>p.trim().match(/^\d{6}$/)).length} valid pincodes entered
                    </div>
                    <button style={{ ...S.btn("#6366f1"), width:"100%", padding:"10px", fontSize:13, opacity:aiLoading?0.7:1 }}
                      onClick={predictBatch} disabled={aiLoading}>
                      {aiLoading ? "🤖 Analysing…" : "🤖 Analyse All Pincodes"}
                    </button>
                  </div>
                </div>
              )}

              {/* Info card */}
              <div style={{ background:"#f5f3ff", borderRadius:10, padding:"12px 14px", border:"1px solid #ddd6fe" }}>
                <div style={{ fontWeight:700, fontSize:12, color:"#6366f1", marginBottom:6 }}>🤖 What AI predicts</div>
                {["Zone detection (metro/tier-2/remote)","Best courier for this route","Estimated delivery days (min-max)","Delay probability %","RTO risk level","COD success rate","Customer-ready ETA message","Tips to reduce delays"].map((t,i)=>(
                  <div key={i} style={{ fontSize:11, color:"#6b7280", display:"flex", gap:5, marginBottom:3 }}>
                    <span style={{ color:"#6366f1" }}>•</span>{t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Results */}
            <div>
              {/* Single prediction result */}
              {!batchMode && !aiLoading && aiDelay && (() => {
                const pa = aiDelay.pincodeAnalysis;
                const preds = aiDelay.courierPredictions || [];
                const best = preds.find((p)=>p.recommended) || preds[0];
                const riskColors = { low:["#15803d","#f0fdf4"], medium:["#d97706","#fffbeb"], high:["#dc2626","#fef2f2"] };
                const zoneColors = { metro:"#2563eb", tier1:"#7c3aed", tier2:"#0ea5e9", tier3:"#F47920", remote:"#dc2626", hilly:"#6b7280", northeast:"#9f2089" };

                return (
                  <div>
                    {/* Pincode analysis banner */}
                    <div style={{ background:"linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius:10, padding:"16px 18px", marginBottom:14, color:"#fff" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>
                            📍 {pa?.city}, {pa?.state} — {pa?.pincode}
                          </div>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            <span style={{ background:zoneColors[pa?.zone]||"#6b7280", color:"#fff", padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700, textTransform:"capitalize" }}>{pa?.zone}</span>
                            <span style={{ background:"rgba(255,255,255,0.2)", padding:"2px 10px", borderRadius:20, fontSize:11 }}>RTO Risk: {pa?.rtoRisk?.toUpperCase()}</span>
                            <span style={{ background:"rgba(255,255,255,0.2)", padding:"2px 10px", borderRadius:20, fontSize:11 }}>COD Success: {pa?.codSuccessRate}</span>
                          </div>
                        </div>
                        <div style={{ background:"#fff", borderRadius:10, padding:"10px 16px", textAlign:"center" }}>
                          <div style={{ fontSize:9, color:"#6b7280", textTransform:"uppercase" }}>Deliverability</div>
                          <div style={{ fontSize:26, fontWeight:800, color:pa?.deliverabilityScore>=80?"#15803d":pa?.deliverabilityScore>=60?"#d97706":"#dc2626" }}>
                            {pa?.deliverabilityScore}/100
                          </div>
                        </div>
                      </div>
                      {pa?.knownIssues?.length > 0 && (
                        <div style={{ marginTop:10 }}>
                          {pa.knownIssues.map((issue,i)=>(
                            <div key={i} style={{ fontSize:11, opacity:.9, display:"flex", gap:5 }}><span>⚠️</span>{issue}</div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ETA advice + customer message */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                      <div style={{ background:"#f0fdf4", borderRadius:8, padding:"12px 14px", border:"1px solid #bbf7d0" }}>
                        <div style={{ fontWeight:700, fontSize:12, color:"#15803d", marginBottom:6 }}>📅 Recommended ETA for Customers</div>
                        <div style={{ fontSize:13, color:"#374151" }}>{aiDelay.etaAdvice}</div>
                      </div>
                      <div style={{ background:"#eff6ff", borderRadius:8, padding:"12px 14px", border:"1px solid #bfdbfe" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                          <div style={{ fontWeight:700, fontSize:12, color:"#2563eb" }}>💬 Customer Message Template</div>
                          <button onClick={()=>navigator.clipboard.writeText(aiDelay.customerMessageTemplate).then(()=>toast("Copied ✓","success"))}
                            style={{ ...S.btnOut, fontSize:10, padding:"1px 7px" }}>Copy</button>
                        </div>
                        <div style={{ fontSize:11, color:"#374151", lineHeight:1.5 }}>{aiDelay.customerMessageTemplate}</div>
                      </div>
                    </div>

                    {/* Courier comparison table */}
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 }}>
                      <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>
                        🚚 Courier-wise Predictions
                      </div>
                      <div style={{ overflowX:"auto" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse" }}>
                          <thead><tr>
                            {["Courier","Est. Days","Delay Prob.","On-time Score","RTO Rate","Est. Cost","Recommendation"].map((h)=>(
                              <th key={h} style={{ ...S.th, whiteSpace:"nowrap" }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {preds.map((p,i)=>(
                              <tr key={i} style={{ background:p.recommended?"#f5f3ff":"" }}
                                onMouseEnter={(e)=>e.currentTarget.style.background=p.recommended?"#ede9fe":"#fafafa"}
                                onMouseLeave={(e)=>e.currentTarget.style.background=p.recommended?"#f5f3ff":""}>
                                <td style={{ ...S.td, fontWeight:700 }}>
                                  {p.recommended && <span style={{ background:"#6366f1", color:"#fff", fontSize:9, padding:"1px 5px", borderRadius:4, marginRight:4 }}>BEST</span>}
                                  {p.courier}
                                </td>
                                <td style={{ ...S.td, fontWeight:600, color:"#1A3A5C" }}>
                                  {p.minDays}-{p.maxDays} days
                                  <div style={{ fontSize:10, color:"#9ca3af" }}>avg {p.estimatedDays}d</div>
                                </td>
                                <td style={{ ...S.td }}>
                                  <span style={{ background:parseFloat(p.delayProbability)>30?"#fef2f2":parseFloat(p.delayProbability)>15?"#fffbeb":"#f0fdf4", color:parseFloat(p.delayProbability)>30?"#dc2626":parseFloat(p.delayProbability)>15?"#d97706":"#15803d", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:700 }}>
                                    {p.delayProbability}
                                  </span>
                                </td>
                                <td style={{ ...S.td }}>
                                  <div style={{ background:"#f3f4f6", borderRadius:10, height:6, width:80, marginBottom:2 }}>
                                    <div style={{ background:p.onTimeScore>=80?"#15803d":p.onTimeScore>=60?"#d97706":"#dc2626", borderRadius:10, height:6, width:(p.onTimeScore*80/100)+"px" }}/>
                                  </div>
                                  <span style={{ fontSize:10, color:"#6b7280" }}>{p.onTimeScore}/100</span>
                                </td>
                                <td style={{ ...S.td, fontSize:11, color:parseFloat(p.rtoRate)>15?"#dc2626":"#6b7280", fontWeight:parseFloat(p.rtoRate)>15?700:400 }}>{p.rtoRate}</td>
                                <td style={{ ...S.td, fontWeight:600 }}>₹{p.estimatedCost}</td>
                                <td style={{ ...S.td, fontSize:11, color:"#6b7280", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={p.recommendationReason}>
                                  {p.recommendationReason}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Risk factors + Tips */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      {aiDelay.riskFactors?.length > 0 && (
                        <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px" }}>
                          <div style={{ fontWeight:700, fontSize:12, color:"#dc2626", marginBottom:8 }}>⚠️ Risk Factors</div>
                          {aiDelay.riskFactors.map((r,i)=>(
                            <div key={i} style={{ fontSize:11, color:"#6b7280", display:"flex", gap:5, marginBottom:4 }}>
                              <span style={{ color:"#dc2626", flexShrink:0 }}>•</span>{r}
                            </div>
                          ))}
                        </div>
                      )}
                      {aiDelay.tips?.length > 0 && (
                        <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px" }}>
                          <div style={{ fontWeight:700, fontSize:12, color:"#15803d", marginBottom:8 }}>💡 Tips to Reduce Delays</div>
                          {aiDelay.tips.map((t,i)=>(
                            <div key={i} style={{ fontSize:11, color:"#6b7280", display:"flex", gap:5, marginBottom:4 }}>
                              <span style={{ color:"#15803d", flexShrink:0 }}>✓</span>{t}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Batch result */}
              {batchMode && !aiLoading && batchResult && (
                <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>
                    📋 Batch Analysis — {batchResult.length} Pincodes
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>
                        {["Pincode","City","State","Zone","Best Courier","Est. Days","RTO Risk","Score","Notes"].map((h)=>(
                          <th key={h} style={{ ...S.th, whiteSpace:"nowrap" }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {batchResult.map((r,i)=>{
                          const riskColors = { low:"#15803d", medium:"#d97706", high:"#dc2626" };
                          const riskBgs    = { low:"#f0fdf4", medium:"#fffbeb", high:"#fef2f2" };
                          return (
                            <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                              <td style={{ ...S.td, fontFamily:"monospace", fontWeight:700, color:"#F47920" }}>{r.pincode}</td>
                              <td style={{ ...S.td, fontWeight:500 }}>{r.city}</td>
                              <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{r.state}</td>
                              <td style={S.td}>
                                <span style={{ background:"#ede9fe", color:"#6366f1", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700, textTransform:"capitalize" }}>{r.zone}</span>
                              </td>
                              <td style={{ ...S.td, fontWeight:600 }}>{r.bestCourier}</td>
                              <td style={{ ...S.td, fontWeight:700 }}>{r.estimatedDays} days</td>
                              <td style={S.td}>
                                <span style={{ background:riskBgs[r.rtoRisk]||"#f3f4f6", color:riskColors[r.rtoRisk]||"#6b7280", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                                  {r.rtoRisk?.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ ...S.td }}>
                                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                                  <div style={{ background:"#f3f4f6", borderRadius:10, height:5, width:50 }}>
                                    <div style={{ background:r.deliverabilityScore>=80?"#15803d":r.deliverabilityScore>=60?"#d97706":"#dc2626", borderRadius:10, height:5, width:(r.deliverabilityScore/2)+"px" }}/>
                                  </div>
                                  <span style={{ fontSize:10 }}>{r.deliverabilityScore}</span>
                                </div>
                              </td>
                              <td style={{ ...S.td, fontSize:10, color:"#6b7280", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.notes}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!aiLoading && !aiDelay && !batchResult && (
                <div style={{ background:"#fff", borderRadius:10, border:"2px dashed #e5e7eb", padding:60, textAlign:"center" }}>
                  <div style={{ fontSize:52, marginBottom:16 }}>🚚</div>
                  <div style={{ fontWeight:700, fontSize:18, marginBottom:8, color:"#1A3A5C" }}>AI Courier Delay Prediction</div>
                  <div style={{ fontSize:13, color:"#6b7280", maxWidth:400, margin:"0 auto" }}>
                    Enter a delivery pincode and let AI predict which courier performs best, expected delivery days, delay probability and RTO risk for that route.
                  </div>
                </div>
              )}

              {aiLoading && (
                <div style={{ background:"#fff", borderRadius:10, border:"2px solid #6366f1", padding:60, textAlign:"center" }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🤖</div>
                  <div style={{ fontWeight:700, fontSize:16, color:"#6366f1", marginBottom:6 }}>Predicting delivery performance…</div>
                  <div style={{ fontSize:12, color:"#9ca3af" }}>Analysing pincode zone, courier history and route patterns</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
